import { Autocomplete, TextField } from "@mui/material";
import { TableType } from "../components/modules/customer/companies/queries/types";

import { useEffect, useMemo, useState } from "react";
import { GetUserRightsByEmpId } from "../components/modules/asset/users/AssignRights/queries/queries";
import {
  GetUserRightsByEmpIdResult,
  GetUserRightsByEmpIdVar,
} from "../components/modules/asset/users/AssignRights/queries/types";
import { useLazyQuery } from "@apollo/client";
import useLoggedInUserDetails from "../customHooks/useLoggedInUserDetails";
import useUrlParams from "../customHooks/useUrlParams";
import useLabels from "../customHooks/useLabels";

export interface tableTypeOptions {
  label: string;
  value: TableType;
  disabled?: boolean;
}
export interface Props {
  onChange: (newValue: tableTypeOptions) => void;
  value: TableType | null;
  onKeyDown:
    | ((
        event: React.KeyboardEvent<HTMLDivElement> & {
          defaultMuiPrevented?: boolean;
        }
      ) => void)
    | undefined;
  disableClearable?: boolean;
  autoFocus?: boolean;
  filterByUserRights?: boolean;
  label?: string;
  placeholder?: string;
  disableOptions?: TableType[];
}

const TableTypeAutocomplete = ({
  onChange,
  onKeyDown,
  value,
  autoFocus,
  disableClearable,
  label,
  placeholder,
  disableOptions,
}: Props) => {
  const [autoValue, setAutoValue] = useState<tableTypeOptions | null>(null);
  const { company_id, sub_id, siteID, junctionID } = useUrlParams();
  const {
    companyLabel,
    subsidiaryLabel,
    subsidiarySiteLabel,
    itemLabel,
    junctionLabel,
  } = useLabels();
  const tableTypeOptions: tableTypeOptions[] = useMemo(() => {
    return [
      {
        label: junctionLabel,
        value: TableType.JUNCTION,
        disabled: disableOptions?.includes(TableType.JUNCTION),
      },
      {
        label: itemLabel,
        value: TableType.ASSET,
        disabled: disableOptions?.includes(TableType.ASSET),
      },
      {
        label: companyLabel,
        value: TableType.COMPANY,
        disabled: disableOptions?.includes(TableType.COMPANY),
      },
      {
        label: subsidiaryLabel,
        value: TableType.SUBSIDIARY,
        disabled: disableOptions?.includes(TableType.SUBSIDIARY),
      },
      {
        label: subsidiarySiteLabel,
        value: TableType.SITE,
        disabled: disableOptions?.includes(TableType.SITE),
      },
    ].filter((item) => !item.disabled);
  }, [
    companyLabel,
    subsidiaryLabel,
    subsidiarySiteLabel,
    itemLabel,
    junctionLabel,
    disableOptions,
  ]);

  const [options, setOptions] = useState<tableTypeOptions[]>(tableTypeOptions);
  const { customer_id } = useUrlParams();

  const { user_details } = useLoggedInUserDetails();

  const [GetRights, { data: rightsData }] = useLazyQuery<
    GetUserRightsByEmpIdResult,
    GetUserRightsByEmpIdVar
  >(GetUserRightsByEmpId);

  useEffect(() => {
    const fetchData = async () => {
      setOptions(tableTypeOptions);
      if (user_details.id && customer_id) {
        GetRights({
          variables: {
            empID: user_details.id,
            customerID: customer_id,
          },
        }).then(({ data, error }) => {
          if (data && data.GetUserRightsByEmpId) {
            const tableTypeIndex = tableTypeOptions
              .map(({ value }) => value)
              .indexOf(data.GetUserRightsByEmpId.tableLevel);
            const filterOptions = (
              tableTypeOptions: tableTypeOptions,
              index: number
            ) => {
              if (index >= tableTypeIndex) {
                return true;
              } else {
                switch (tableTypeOptions.value) {
                  case TableType.COMPANY:
                    return company_id ? false : true;
                  case TableType.SUBSIDIARY:
                    return sub_id ? false : true;
                  case TableType.SITE:
                    return siteID ? false : true;
                  case TableType.JUNCTION:
                    return junctionID ? false : true;
                  case TableType.ASSET:
                    return true;
                  default:
                    return false;
                }
              }
            };

            setOptions(tableTypeOptions.filter(filterOptions));
          }
          if (error) {
            if (error.message.includes("user_rights_master not found")) {
              const filterOptions = (
                tableTypeOptions: tableTypeOptions,
                index: number
              ) => {
                switch (tableTypeOptions.value) {
                  case TableType.COMPANY:
                    return company_id ? false : true;
                  case TableType.SUBSIDIARY:
                    return sub_id ? false : true;
                  case TableType.SITE:
                    return siteID ? false : true;
                  case TableType.JUNCTION:
                    return junctionID ? false : true;
                  case TableType.ASSET:
                    return true;
                  default:
                    return false;
                }
              };

              setOptions(tableTypeOptions.filter(filterOptions));
            }
          }
        });
      }
    };
    fetchData();
  }, [
    user_details.id,
    company_id,
    sub_id,
    siteID,
    junctionID,
    rightsData,
    tableTypeOptions,
    customer_id,
    GetRights,
  ]);

  useEffect(() => {
    if (value) {
      const foundValue = tableTypeOptions.find(
        ({ value: optionsValue }) => optionsValue === value
      );
      setAutoValue(foundValue ? foundValue : null);
    } else {
      setAutoValue(null);
    }
  }, [tableTypeOptions, value]);

  return (
    <div className="multi-select-autocomplete">
      <Autocomplete
        options={options}
        onKeyDown={onKeyDown}
        value={autoValue}
        openOnFocus
        forcePopupIcon
        getOptionLabel={(option) => (option as tableTypeOptions).label}
        onChange={(e, newValue) => {
          onChange(newValue as tableTypeOptions);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            label={label}
            placeholder={placeholder}
            autoFocus={autoFocus}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        )}
      />
    </div>
  );
};

export default TableTypeAutocomplete;
